/**
 * Continental-US landmass and state borders as SVG path data.
 *
 * The concentration plot drew exposure bubbles onto an empty rounded rectangle:
 * correctly positioned dots floating in a box, with nothing to say whether a
 * marker sat in Nevada or the Pacific. This supplies the ground underneath.
 *
 * Local and free by construction. `us-atlas` ships the Census Bureau's own
 * 1:10m cartographic boundaries as a 114KB TopoJSON in the bundle — no tile
 * server, no API key, no request leaves the browser, and nothing outside
 * Helios learns which markets the book is concentrated in.
 *
 * Deliberately not a tiled basemap. At country scale a road network behind the
 * bubbles is noise competing with the data, and the panel needs an outline, not
 * a map you navigate. The deal dossier is the opposite case and stays on Google
 * Maps: its value there is aerial imagery and Street View, neither of which has
 * a free equivalent at a resolution an underwriter can use.
 *
 * The projection is passed in rather than reimplemented. Geography and markers
 * must be placed by the same function or the coastline drifts away from the
 * dots — a map that is subtly, invisibly wrong about where a deal is.
 */
import { feature } from 'topojson-client'
import type { FeatureCollection, Geometry, MultiPolygon, Polygon } from 'geojson'
import type { Topology } from 'topojson-specification'
import states10m from 'us-atlas/states-10m.json'

/** Longitude/latitude to plot coordinates, supplied by the caller. */
export type Projector = (longitude: number, latitude: number) => { x: number; y: number }

/**
 * States outside the continental frame.
 *
 * Alaska, Hawaii and the territories are dropped rather than clipped: the plot
 * declares a continental-US window, and a partial Aleutian arc smeared along
 * the left edge reads as a rendering fault rather than as a deliberate scope.
 * The panel already counts non-continental deals separately.
 */
const NON_CONTINENTAL = new Set([
  'Alaska',
  'Hawaii',
  'Puerto Rico',
  'United States Virgin Islands',
  'American Samoa',
  'Guam',
  'Commonwealth of the Northern Mariana Islands',
])

/** Rings below this are dropped: barrier islands, river islets, and — the only
 *  whole feature affected — the District of Columbia, whose ~10-point boundary
 *  renders as a single pixel at country scale. All 48 continental states draw.
 *  Verified against us-atlas 3.0.1; re-check if that data is upgraded. */
const MIN_RING_POINTS = 12

type Ring = number[][]

function ringToPath(ring: Ring, project: Projector, precision: number): string | null {
  if (ring.length < MIN_RING_POINTS) return null
  const segments: string[] = []
  let previous: { x: number; y: number } | null = null
  for (const position of ring) {
    const [longitude, latitude] = position
    const point = project(longitude, latitude)
    const x = Number(point.x.toFixed(precision))
    const y = Number(point.y.toFixed(precision))
    // Successive coordinates routinely collapse onto the same rendered pixel at
    // this scale; emitting them anyway triples the path string for no visible
    // difference.
    if (previous && previous.x === x && previous.y === y) continue
    segments.push(`${segments.length === 0 ? 'M' : 'L'}${x} ${y}`)
    previous = { x, y }
  }
  return segments.length < 3 ? null : `${segments.join('')}Z`
}

function geometryToPath(geometry: Geometry, project: Projector, precision: number): string {
  const polygons: Ring[][] =
    geometry.type === 'Polygon'
      ? [(geometry as Polygon).coordinates]
      : geometry.type === 'MultiPolygon'
        ? (geometry as MultiPolygon).coordinates
        : []
  const parts: string[] = []
  for (const polygon of polygons) {
    for (const ring of polygon) {
      const path = ringToPath(ring, project, precision)
      if (path) parts.push(path)
    }
  }
  return parts.join('')
}

/** Decoded once: the topology is static, and re-deriving it per render would
 *  re-walk 114KB of arcs on every board refresh. */
const collection = feature(
  states10m as unknown as Topology,
  (states10m as unknown as Topology).objects.states,
) as FeatureCollection

const continental = collection.features.filter(
  (item) => !NON_CONTINENTAL.has(String(item.properties?.name ?? '')),
)

export interface UsGeography {
  /** One path per state, for the border hairlines. */
  states: { name: string; d: string }[]
  /** Every state path concatenated, for a single filled landmass. Drawn as one
   *  shape so interior borders do not double-strike and darken. */
  landmass: string
}

const cache = new Map<string, UsGeography>()

/**
 * Build the paths for one projection.
 *
 * Keyed by the caller's cache key (its viewport), because the projection is a
 * closure and cannot be compared. A board that never resizes builds these once.
 */
export function usGeography(
  project: Projector,
  cacheKey: string,
  precision = 1,
): UsGeography {
  const hit = cache.get(cacheKey)
  if (hit) return hit

  const paths = continental
    .map((item) => ({
      name: String(item.properties?.name ?? ''),
      d: geometryToPath(item.geometry, project, precision),
    }))
    .filter((item) => item.d.length > 0)

  const built: UsGeography = {
    states: paths,
    landmass: paths.map((item) => item.d).join(''),
  }
  cache.set(cacheKey, built)
  return built
}
